import {
  useFloorPlan,
  useTables,
  useZones,
} from '@/features/reservation-setup/hooks/useReservationSetup';
import { useIsLargeScreen } from '@/utils/useIsLargeScreen';
import { useT } from '@/i18n';
import { Block, BlockTitle, List, ListItem, Navbar, Page, Preloader } from 'framework7-react';

function FloorPlanPage() {
  const t = useT();
  const rs = t.reservationSetup;
  const isLarge = useIsLargeScreen();
  const { data: floorPlan, isLoading: loadingPlan } = useFloorPlan();
  const { data: tables, isLoading: loadingTables } = useTables();
  const { data: zones } = useZones();

  const zoneLabel = (zoneId: string | null) => zones?.find((z) => z.id === zoneId)?.label ?? null;

  if (loadingPlan || loadingTables) {
    return (
      <Page name="floor-plan">
        <Navbar title={rs.floorPlan} backLink={t.back} />
        <Block className="text-align-center">
          <Preloader />
        </Block>
      </Page>
    );
  }

  const placedTableIds = new Set(
    (floorPlan?.layout.elements ?? [])
      .filter((el): el is Extract<typeof el, { kind: 'table' }> => el.kind === 'table')
      .map((el) => el.tableId),
  );
  const allTables = tables ?? [];
  const placed = allTables.filter((t) => placedTableIds.has(t.id));
  const unplaced = allTables.filter((t) => !placedTableIds.has(t.id));

  return (
    <Page name="floor-plan">
      <Navbar title={rs.floorPlan} backLink={t.back} />

      <Block strongIos strongMd outlineIos>
        {isLarge ? (
          <>
            <p>{rs.dragDropEditor}</p>
            <p>{rs.floorPlanVersion(floorPlan?.version ?? 0, placed.length, allTables.length)}</p>
          </>
        ) : (
          <p>{rs.editorLargerScreen}</p>
        )}
      </Block>

      <BlockTitle>{rs.placed(placed.length)}</BlockTitle>
      {placed.length === 0 ? (
        <Block strongIos strongMd outlineIos>
          {rs.noTablesPlaced}
        </Block>
      ) : (
        <List strongIos strongMd outlineIos dividersIos dividersMd>
          {placed.map((tbl) => (
            <ListItem
              key={tbl.id}
              title={[zoneLabel(tbl.zoneId), tbl.label].filter(Boolean).join(' · ')}
              after={rs.seatsCount(tbl.seats)}
            />
          ))}
        </List>
      )}

      {unplaced.length > 0 && (
        <>
          <BlockTitle>{rs.notPlaced(unplaced.length)}</BlockTitle>
          <List strongIos strongMd outlineIos dividersIos dividersMd>
            {unplaced.map((tbl) => (
              <ListItem
                key={tbl.id}
                title={[zoneLabel(tbl.zoneId), tbl.label].filter(Boolean).join(' · ')}
                after={rs.seatsCount(tbl.seats)}
              />
            ))}
          </List>
        </>
      )}
    </Page>
  );
}

export default FloorPlanPage;
